import { useSetConfirm } from "@/shared/atoms/useConfirm";
import { useSetModal } from "@/shared/atoms/useModal";
import { useSetPosOrderPayment } from "@/shared/atoms/usePosOrderPaymentAtom";
import usePosOrderTable from "@/shared/atoms/usePosOrderTable";
import { useSetPosShowPayment } from "@/shared/atoms/usePosShowPayment";
import useCommon from "@/shared/hooks/useCommon";
import {
  useHasOrders,
  useSessionCurrentTableIds,
  useSessionId,
  useShowPayments,
} from "@/shared/hooks/usePosOrderTableSessionHook";
import useResetTableHook from "@/shared/hooks/useResetTableHook";
import useOrderSessionStatusMutation from "@/shared/mutations/orderSessions/useOrderSessionStatusMutation";
import { PaymentMethodEnum } from "@/shared/utils/enum";
import { asArray } from "@/shared/utils/helpers";
import Tooltip from "@/Components/Tooltip/Tooltip";
import IconButton from "../IconButton/IconButton";
import PosSessionBackButton from "./PosSessionBackButton";
import PosTakeoutButton from "./PosTakeoutButton";
import { useSetMobileCartShow } from "@/shared/atoms/useMobileCartShow";
import { useSetWaiterView } from "@/shared/atoms/useWaiterView";
import useIsWaiter from "@/shared/hooks/useIsWaiter";
import useIsMobileHook from "@/shared/hooks/useIsMobileHook";
import useIsTabletHook from "@/shared/hooks/useIsTabletHook";

const PosCartSessionMenu = () => {
  const setShowPayment = useSetPosShowPayment();
  const [table] = usePosOrderTable();
  const sessionId = useSessionId();
  const showPayments = useShowPayments();
  const hasOrders = useHasOrders();
  const currentTableIds = useSessionCurrentTableIds();
  const mutate = useOrderSessionStatusMutation();
  const t = useCommon();
  const setPayment = useSetPosOrderPayment();
  const setModal = useSetModal();
  const confirm = useSetConfirm();
  const { resetTable, destroySession, destroy } = useResetTableHook();
  const isWaiter = useIsWaiter();
  const isMobile = useIsMobileHook();
  const isTablet = useIsTabletHook();
  const setShowCartForMobile = useSetMobileCartShow();
  const setWaiterView = useSetWaiterView();

  const handleSessionBack = () => {
    if (isWaiter && (isMobile || isTablet) && hasOrders) {
      setShowCartForMobile(false);
      setWaiterView("menu");
      return;
    }
    resetTable();
  };

  return (
    <div className="flex items-center justify-between px-4 py-6 border-b-2 border-border/50 overflow-x-auto overflow-hidden">
      <PosSessionBackButton table={table} onClick={handleSessionBack} />
      {hasOrders && (
        <Tooltip content={t("payOrder")}>
          <div>
            <div className="flex items-center">
              <IconButton
                isPending={mutate.isPending}
                onClick={() => {
                  setShowPayment((c) => !c);
                  setPayment(
                    showPayments
                      ? PaymentMethodEnum.PAYMENT_METHOD_NOT_KNOWN_YET
                      : PaymentMethodEnum.CASH
                  );
                }}
                variants="money"
                size="small"
              />
            </div>
          </div>
        </Tooltip>
      )}

      <Tooltip content={t("changeTable")}>
        <div>
          <IconButton
            onClick={() => {
              setModal("changeTables", {
                session_id: sessionId,
                current_table_ids: asArray(currentTableIds),
              });
            }}
            variants="exchange"
            size="small"
          />
        </div>
      </Tooltip>

      <Tooltip content={t("mergeTables")}>
        <div>
          <IconButton
            onClick={() => {
              setModal("mergeSessions", { session_id: sessionId });
            }}
            variants="link"
            size="small"
          />
        </div>
      </Tooltip>

      <div>
        <PosTakeoutButton />
      </div>
      <Tooltip content={t("destroySession")}>
        <div>
          <IconButton
            isPending={destroy.isPending}
            variant="danger"
            variants="trash"
            onClick={() =>
              confirm({
                message: t("confirmationMessage"),
                cb: () => destroySession(),
              })
            }
            size="small"
          />
        </div>
      </Tooltip>
    </div>
  );
};

export default PosCartSessionMenu;
